import clsx from "clsx";
import { useAtomValue } from "jotai";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { animationsAtom } from "../../../../atoms";
import { SmetaSplitterElement } from "../../../ComponentInterface";
import SmetaSplitterBar from "./SmetaSplitterBar";
import {
  getChildren,
  getDefaultCollapseSide,
  getMeasurements,
  Measurements,
  ObjectDragDetails,
  smetaSplitterBarSize,
  SmetaSplitterItemLimits,
  SmetaSplitterItemProps,
  SmetaSplitterProps,
} from "./SmetaSplitterConsts";
import {
  SplitterContext,
  SplitterContextType,
  SplitterIndexContext,
  SplitterItemPropertyName,
  SplitterItemPropertyValue,
} from "./SmetaSplitterContext";
import styles from "./splitter.module.css";

// eslint-disable-next-line @typescript-eslint/naming-convention
const SmetaSplitter = (props: SmetaSplitterProps) => {
  const splitterRef = useRef<SmetaSplitterElement>(null!);
  const sizeSplitterContainer = useRef({ width: 0, height: 0 });
  const splitterItems = useRef<HTMLDivElement[]>([]);
  const splitterBars = useRef<HTMLDivElement[]>([]);
  const measurements = useRef<Measurements>(undefined!);
  const resizeEventFired = useRef(false);
  const noNeighbourValidation = useRef(false);
  const splitterSize = useRef(0);
  const sizeLimitsItems = useRef<Record<number, SmetaSplitterItemLimits>>({});
  const sizeItemsBeforeCollapse = useRef<Record<number, number>>({});
  const originalSizeItems = useRef<Record<number, string | number>>({});
  const currentSizeItems = useRef<Record<number, number>>({});
  const collapsedSplitterItems = useRef<Record<number, boolean>>({});
  const minMaxSizeItems = useRef<
    Record<number, { min: string; _min?: string; max: string; _max?: string; size: string }>
  >({});
  const paddingsItems = useRef<Record<number, number>>({});
  const neighbourItemItems = useRef<Record<number, number>>({});
  const ignorePropertyValueItems = useRef<Record<number, boolean>>({});
  const collapseButtonPressed = useRef<{
    splitBar: HTMLDivElement;
    item: number;
    target: HTMLButtonElement;
    farCollapse: boolean;
  }>(undefined);
  const dragDetails = useRef<ObjectDragDetails>(undefined);
  const splitBarDummy = useRef<HTMLDivElement>(undefined);
  const splitterBackgroundFeedback = useRef<HTMLDivElement>(undefined);
  const animationsOn = useAtomValue(animationsAtom);
  const splitterIsInitializing = useRef(true);
  const primaryElement = props.primaryIndex === "end" ? "end" : "start";
  const transitionDuration = props.transitionDuration !== undefined ? props.transitionDuration : 250;
  const resizeTimeout = useRef<NodeJS.Timeout>(undefined);

  const propertyChangedHandler: SplitterContextType = useCallback(
    (
      index: number,
      propertyName: SplitterItemPropertyName,
      oldValue?: SplitterItemPropertyValue,
      newValue?: SplitterItemPropertyValue,
    ) => {
      if (propertyName === "collapsed") {
        if (collapsedSplitterItems.current[index] === Boolean(newValue)) {
          delete ignorePropertyValueItems.current[index];
          return;
        }
        ignorePropertyValueItems.current[index] = true;
        if (newValue) {
          collapse(index);
        } else expand(index);
      } else {
        const value = typeof newValue === "string" ? newValue : "";
        setSize(propertyName, value, index);
      }
    },
    [],
  );

  useEffect(() => {
    if (props.hideSplitterCollapsed) {
      splitterRef.current.setAttribute("data-hideSplitterCollapsed", "");
    } else splitterRef.current.removeAttribute("data-hideSplitterCollapsed");
  }, [props.hideSplitterCollapsed]);

  useLayoutEffect(() => {
    measurements.current = getMeasurements(props.orientation);
  }, [props.orientation]);

  useLayoutEffect(() => {
    splitterItems.current = [];
    splitterBars.current = [];
    Array.from(splitterRef.current.children[0].children).forEach((child) => {
      if (child instanceof HTMLDivElement)
        if (child.classList.contains(styles.splitterItem)) {
          splitterItems.current.push(child);
        } else {
          splitterBars.current.push(child);
        }
    });
  }, [props.children]);

  useLayoutEffect(() => {
    const splitterContainer = splitterRef.current;
    splitterRef.current.resize = handleResize;
    splitterRef.current.expand = splitterExpand;
    splitterRef.current.collapse = splitterCollapse;
    sizeSplitterContainer.current = {
      width: splitterContainer.offsetWidth,
      height: splitterContainer.offsetHeight,
    };
    const childrenArray = getChildren(props.children)!;
    splitterItems.current.forEach((item, i) => {
      const childProps = (childrenArray[i] as React.ReactElement<SmetaSplitterItemProps>).props;
      if (primaryElement === "end" && i === childrenArray.length - 1) {
        collapsedSplitterItems.current[i] = false;
      } else if (primaryElement === "start" && i === 0) {
        collapsedSplitterItems.current[i] = false;
      } else collapsedSplitterItems.current[i] = childProps.collapsed ?? childProps.defaultCollapsed ?? false;
      minMaxSizeItems.current[i] = {
        min: childProps.min ?? "",
        max: childProps.max ?? "",
        size: childProps.size ?? "",
      };
    });
    handleSplitterBars();
    splitterItems.current.forEach((item, i) => {
      if (minMaxSizeItems.current[i].min) setSize("min", minMaxSizeItems.current[i].min, i);
      if (minMaxSizeItems.current[i].max) setSize("max", minMaxSizeItems.current[i].max, i);
    });
    validateItemSize();
    splitterIsInitializing.current = false;
    const resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(splitterContainer);
    window.addEventListener("pointerup", documentUpHandler);
    window.addEventListener("pointermove", resize);
    return () => {
      resizeObserver.unobserve(splitterContainer);
      resizeObserver.disconnect();
      window.removeEventListener("pointerup", documentUpHandler);
      window.removeEventListener("pointermove", resize);
    };
  }, []);

  const getNonPrimaryElementIndex = (item?: number) => {
    const totalItems = splitterItems.current.length;
    if (totalItems < 2) return undefined;
    if (item === undefined) return primaryElement === "start" ? 1 : totalItems - 2;
    const primaryIndex = primaryElement === "start" ? 0 : totalItems - 1;
    if (item === primaryIndex) return undefined;
    if (item < 0 || item >= totalItems) return undefined;
    return item;
  };

  const getAvailableSplitterSize = () => {
    const barsSize = splitterBars.current.reduce((sum, bar) => sum + bar[measurements.current.size], 0);
    return Math.max(0, splitterRef.current[measurements.current.size] - barsSize);
  };

  const splitterCollapse = (needToDo?: boolean, item?: number) => {
    const itemIndex = getNonPrimaryElementIndex(item);
    if (itemIndex !== undefined) {
      collapse(itemIndex);
      if (needToDo)
        setTimeout(
          () => {
            props.onCollapse?.(splitterRef.current);
          },
          animationsOn ? transitionDuration + 1 : 0,
        );
    }
  };

  const splitterExpand = (needToDo?: boolean, item?: number) => {
    const itemIndex = getNonPrimaryElementIndex(item);
    if (itemIndex !== undefined) {
      expand(itemIndex);
      if (needToDo)
        setTimeout(
          () => {
            props.onExpand?.(splitterRef.current);
          },
          animationsOn ? transitionDuration + 1 : 0,
        );
    }
  };

  const collapse = (itemIndex: number, isNearSide?: boolean) => {
    const item = splitterItems.current[itemIndex];
    if (!ignorePropertyValueItems.current[itemIndex] && collapsedSplitterItems.current[itemIndex]) return;
    if (itemIndex === splitterItems.current.length - 1) {
      isNearSide = true;
    } else if (itemIndex === 0) {
      isNearSide = false;
    }
    const direction = isNearSide ? -1 : 1;
    let neighbourIndex = itemIndex + direction;
    let neighbour = splitterItems.current[neighbourIndex];
    while (neighbour && collapsedSplitterItems.current[neighbourIndex]) {
      neighbourIndex += direction;
      neighbour = splitterItems.current[neighbourIndex];
    }
    if (!neighbour) {
      collapsedSplitterItems.current[itemIndex] = false;
      return;
    }
    delete ignorePropertyValueItems.current[itemIndex];
    if (!sizeItemsBeforeCollapse.current[itemIndex]) {
      sizeItemsBeforeCollapse.current[itemIndex] = item[measurements.current.size];
    }
    if (!sizeItemsBeforeCollapse.current[neighbourIndex]) {
      sizeItemsBeforeCollapse.current[neighbourIndex] = neighbour[measurements.current.size];
    }
    const splitterBar =
      primaryElement === "end" ? splitterBars.current[itemIndex] : splitterBars.current[itemIndex - 1];
    const totalSize = neighbour[measurements.current.size] + item[measurements.current.size];
    const minSize =
      !props.hideSplitterCollapsed && splitterBar?.[measurements.current.size] !== undefined
        ? splitterBar[measurements.current.size]
        : sizeLimitsItems.current[itemIndex][measurements.current.minDimension];
    const hiddensBarsSize = props.hideSplitterCollapsed ? smetaSplitterBarSize : 0;
    const targetSize = totalSize - minSize;
    if (totalSize && targetSize < minSize) {
      collapsedSplitterItems.current[itemIndex] = false;
      return;
    }
    if (animationsOn && !splitterIsInitializing.current) {
      splitterRef.current.classList.add(styles.animate);
      if (!item.style[measurements.current.dimension]) {
        item.style[measurements.current.dimension] = item[measurements.current.size] + "px";
      }
      item.style.transition = `all ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      neighbour.style.transition = `all ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      setTimeout(() => {
        splitterRef.current.classList.remove(styles.animate);
        item.style.transition = "";
        neighbour.style.transition = "";
      }, transitionDuration + 1);
    }
    if (!paddingsItems.current[itemIndex]) {
      const computed = getComputedStyle(item);
      const startPadding = parseFloat(computed.getPropertyValue("padding-" + measurements.current.position)) || 0;
      const endPadding = parseFloat(computed.getPropertyValue("padding-" + measurements.current.position2)) || 0;
      paddingsItems.current[itemIndex] = startPadding + endPadding;
    }
    if (sizeItemsBeforeCollapse.current[neighbourIndex] !== undefined) {
      sizeItemsBeforeCollapse.current[neighbourIndex] += sizeItemsBeforeCollapse.current[itemIndex];
    }
    if (
      sizeLimitsItems.current[neighbourIndex]?.[measurements.current.maxDimension] &&
      totalSize > sizeLimitsItems.current[neighbourIndex][measurements.current.maxDimension]
    ) {
      sizeLimitsItems.current[neighbourIndex].ignoreUpdate = true;
      neighbour.style[measurements.current.maxDimension] = sizeItemsBeforeCollapse.current[neighbourIndex]
        ? sizeItemsBeforeCollapse.current[neighbourIndex] + hiddensBarsSize + "px"
        : "";
    }
    if (sizeItemsBeforeCollapse.current[neighbourIndex]) {
      neighbour.style[measurements.current.dimension] =
        sizeItemsBeforeCollapse.current[neighbourIndex] + hiddensBarsSize + "px";
    }
    item.style[measurements.current.dimension] = item.style[measurements.current.minDimension] = "0";
    item.style.padding = "0";
    neighbourItemItems.current[itemIndex] = neighbourIndex;
    collapsedSplitterItems.current[itemIndex] = true;
    splitterBar.setAttribute("data-itemcollapsed", "");
  };

  const expand = (itemIndex: number) => {
    const item = splitterItems.current[itemIndex];
    if (ignorePropertyValueItems.current[itemIndex] || collapsedSplitterItems.current[itemIndex]) {
      if (!splitterItems.current[neighbourItemItems.current[itemIndex]]) {
        collapsedSplitterItems.current[itemIndex] = true;
        return;
      }
      let neighbourItemIndex = neighbourItemItems.current[itemIndex];
      delete ignorePropertyValueItems.current[itemIndex];
      if (!ignorePropertyValueItems.current[neighbourItemIndex] && collapsedSplitterItems.current[neighbourItemIndex]) {
        const direction = itemIndex > neighbourItemIndex ? -1 : 1;
        do {
          neighbourItemIndex += direction;
          neighbourItemItems.current[itemIndex] = neighbourItemIndex;
        } while (splitterItems.current[neighbourItemIndex] && collapsedSplitterItems.current[neighbourItemIndex]);
      }
      if (!splitterItems.current[neighbourItemIndex]) {
        collapsedSplitterItems.current[itemIndex] = true;
        return;
      }
      if (minMaxSizeItems.current[itemIndex].min)
        setSize("min", minMaxSizeItems.current[itemIndex].min, itemIndex, true);
      const otherVisibleItemsSize = splitterItems.current.reduce((sum, splitterItem, index) => {
        if (index === itemIndex || index === neighbourItemIndex || collapsedSplitterItems.current[index]) {
          return sum;
        }
        return sum + splitterItem[measurements.current.size];
      }, 0);
      const availablePairSize = Math.max(0, getAvailableSplitterSize() - otherVisibleItemsSize);
      const neighbourSizeBeforeCollapse = availablePairSize;
      const minSize = sizeLimitsItems.current[itemIndex][measurements.current.minDimension];
      const neighbourMinSize = sizeLimitsItems.current[neighbourItemIndex][measurements.current.minDimension];
      const availableSize = neighbourSizeBeforeCollapse - minSize;
      if (neighbourSizeBeforeCollapse && availableSize < neighbourMinSize) {
        collapsedSplitterItems.current[itemIndex] = true;
        return;
      }
      if (!paddingsItems.current[neighbourItemIndex]) {
        const neighbourStyle = getComputedStyle(splitterItems.current[neighbourItemIndex]);
        paddingsItems.current[neighbourItemIndex] =
          (parseFloat(neighbourStyle.getPropertyValue("padding-" + measurements.current.position)) || 0) +
          (parseFloat(neighbourStyle.getPropertyValue("padding-" + measurements.current.position2)) || 0);
      }
      if (!paddingsItems.current[itemIndex]) {
        const itemStyle = getComputedStyle(item);
        paddingsItems.current[itemIndex] =
          (parseFloat(itemStyle.getPropertyValue("padding-" + measurements.current.position)) || 0) +
          (parseFloat(itemStyle.getPropertyValue("padding-" + measurements.current.position2)) || 0);
      }
      if (
        typeof minMaxSizeItems.current[itemIndex].size === "string" &&
        minMaxSizeItems.current[itemIndex].size.includes("%")
      ) {
        sizeItemsBeforeCollapse.current[itemIndex] =
          (getAvailableSplitterSize() * parseFloat(minMaxSizeItems.current[itemIndex].size)) / 100;
      }
      const newSize = Math.min(
        Math.max(minSize, sizeItemsBeforeCollapse.current[itemIndex]),
        neighbourSizeBeforeCollapse -
          paddingsItems.current[neighbourItemIndex] -
          paddingsItems.current[itemIndex] -
          neighbourMinSize,
      );
      if (newSize < 0) {
        collapsedSplitterItems.current[itemIndex] = true;
        return;
      }
      if (minMaxSizeItems.current[itemIndex].min) item.style[measurements.current.minDimension] = "0";
      if (animationsOn && !splitterIsInitializing.current) {
        splitterRef.current.classList.add(styles.animate);
        item.style.transition = `all ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        splitterItems.current[neighbourItemIndex].style.transition =
          `all ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        setTimeout(() => {
          splitterRef.current.classList.remove(styles.animate);
          item.style.transition = "";
          splitterItems.current[neighbourItemIndex].style.transition = "";
        }, transitionDuration + 1);
      }
      item.style.padding = "";
      if (minMaxSizeItems.current[itemIndex].min) {
        item.style[measurements.current.minDimension] =
          sizeLimitsItems.current[itemIndex][measurements.current.minDimension] + "px";
      }
      sizeItemsBeforeCollapse.current[itemIndex] = newSize;
      item.style[measurements.current.dimension] = newSize + "px";
      sizeItemsBeforeCollapse.current[neighbourItemIndex] = Math.max(
        sizeLimitsItems.current[neighbourItemIndex][measurements.current.minDimension],
        neighbourSizeBeforeCollapse - newSize,
      );
      splitterItems.current[neighbourItemIndex].style[measurements.current.dimension] =
        sizeItemsBeforeCollapse.current[neighbourItemIndex] + "px";
      if (sizeLimitsItems.current[neighbourItemIndex][measurements.current.maxDimension]) {
        splitterItems.current[neighbourItemIndex].style[measurements.current.maxDimension] =
          sizeLimitsItems.current[neighbourItemIndex][measurements.current.maxDimension] + "px";
      }
      collapsedSplitterItems.current[itemIndex] = false;
      if (itemIndex > neighbourItemItems.current[itemIndex]) {
        item.previousElementSibling!.removeAttribute("data-itemcollapsed");
      } else {
        item.nextElementSibling!.removeAttribute("data-itemcollapsed");
      }
      delete neighbourItemItems.current[itemIndex];
    }
  };

  const validateSize = (itemIndex: number) => {
    if (collapsedSplitterItems.current[itemIndex]) return;
    const item = splitterItems.current[itemIndex];
    const currentSize = sizeItemsBeforeCollapse.current[itemIndex]
      ? sizeItemsBeforeCollapse.current[itemIndex]
      : item.getBoundingClientRect()[measurements.current.dimension];
    const validateLimit = (type: "min" | "max") => {
      const dimensionKey = type === "min" ? "minDimension" : "maxDimension";
      const styleValue = item.style[measurements.current[dimensionKey]];
      const sizeLimit = sizeLimitsItems.current[itemIndex][measurements.current[dimensionKey]];
      const computedStyle = styleValue;
      const isBelowMin = type === "min" && currentSize < sizeLimit;
      const isAboveMax = type === "max" && currentSize > sizeLimit;
      if (isBelowMin || isAboveMax) {
        item.style[measurements.current.dimension] =
          typeof computedStyle === "string" && computedStyle.includes("%") ? computedStyle : sizeLimit + "px";
        sizeItemsBeforeCollapse.current[itemIndex] = sizeLimit;
      }
    };
    if (minMaxSizeItems.current[itemIndex].min) validateLimit("min");
    if (minMaxSizeItems.current[itemIndex].max) validateLimit("max");
  };

  const validateNeighbourSizeLimits = (itemIndex: number) => {
    function setSizeLimitsForItem(index: number) {
      if (splitterItems.current[index]) {
        if (minMaxSizeItems.current[index].min) setSize("min", minMaxSizeItems.current[index].min, index, true);
        if (minMaxSizeItems.current[index].max) setSize("max", minMaxSizeItems.current[index].max, index, true);
      }
    }
    if (!splitterItems.current.length) return;
    noNeighbourValidation.current = true;
    setSizeLimitsForItem(itemIndex - 1);
    setSizeLimitsForItem(itemIndex + 1);
    noNeighbourValidation.current = false;
  };

  const setSize = (property: "size" | "min" | "max", value: string, itemIndex: number, skipValidation?: boolean) => {
    const item = splitterItems.current[itemIndex];
    const isPercent = typeof value === "string" && value.includes("%");
    const availableSize = getAvailableSplitterSize();
    const toPercent = (px: number) => (100 * px) / availableSize;
    const valueLocal = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
    switch (property) {
      case "size": {
        const prevSize = item[measurements.current.size];
        if (value !== "auto" && value) {
          if (isPercent) {
            item.style[measurements.current.dimension] = valueLocal + "%";
            sizeItemsBeforeCollapse.current[itemIndex] = (valueLocal * availableSize) / 100;
          } else {
            sizeItemsBeforeCollapse.current[itemIndex] = valueLocal;
            item.style[measurements.current.dimension] = sizeItemsBeforeCollapse.current[itemIndex] + "px";
          }
        } else {
          item.style[measurements.current.dimension] = value;
          sizeItemsBeforeCollapse.current[itemIndex] = item[measurements.current.size];
        }
        validateSize(itemIndex);
        const diff = prevSize - sizeItemsBeforeCollapse.current[itemIndex];
        if (originalSizeItems.current[itemIndex]) {
          originalSizeItems.current[itemIndex] = isPercent
            ? valueLocal + "%"
            : sizeItemsBeforeCollapse.current[itemIndex];
        }
        const lastItem = splitterItems.current[splitterItems.current.length - 1];
        if (item === lastItem) {
          let fallbackItemIndex = splitterItems.current.findIndex(
            (it, i) => it !== item && !collapsedSplitterItems.current[i] && !minMaxSizeItems.current[i].size,
          );
          if (fallbackItemIndex === -1) {
            for (let i = splitterItems.current.length - 2; i >= 0; i--) {
              const candidate = splitterItems.current[i];
              if (!collapsedSplitterItems.current[i] && candidate !== item) {
                fallbackItemIndex = i;
                break;
              }
            }
          }
          if (fallbackItemIndex !== -1) {
            sizeItemsBeforeCollapse.current[fallbackItemIndex] = Math.max(
              0,
              splitterItems.current[fallbackItemIndex][measurements.current.size] + diff,
            );
            splitterItems.current[fallbackItemIndex].style[measurements.current.dimension] =
              sizeItemsBeforeCollapse.current[fallbackItemIndex] + "px";
          }
        }
        break;
      }
      case "min": {
        const minValue = isPercent ? (valueLocal * availableSize) / 100 : valueLocal;
        sizeLimitsItems.current[itemIndex].minWidth = sizeLimitsItems.current[itemIndex].minHeight = minValue;
        item.style[measurements.current.minDimension] =
          collapsedSplitterItems.current[itemIndex] || !minValue
            ? ""
            : isPercent
              ? toPercent(minValue) + "%"
              : minValue + "px";
        item.style[("min" + measurements.current.restrictedDimension) as "minHeight" | "minWidth"] = "";
        validateSize(itemIndex);
        if (minValue || !noNeighbourValidation.current) validateNeighbourSizeLimits(itemIndex);
        if (minMaxSizeItems.current[itemIndex].size) {
          setSize("size", minMaxSizeItems.current[itemIndex].size, itemIndex, skipValidation);
          return;
        }
        break;
      }
      case "max": {
        const maxValue = isPercent ? (valueLocal * availableSize) / 100 : valueLocal;
        sizeLimitsItems.current[itemIndex].maxWidth = maxValue;
        sizeLimitsItems.current[itemIndex].maxHeight = maxValue;
        item.style[measurements.current.maxDimension] = maxValue
          ? isPercent
            ? toPercent(maxValue) + "%"
            : maxValue + "px"
          : "";
        item.style[("max" + measurements.current.restrictedDimension) as "maxHeight" | "maxWidth"] = "";
        validateSize(itemIndex);
        if (maxValue || !noNeighbourValidation.current) validateNeighbourSizeLimits(itemIndex);
        if (minMaxSizeItems.current[itemIndex].size) {
          setSize("size", minMaxSizeItems.current[itemIndex].size, itemIndex, skipValidation);
          return;
        }
        break;
      }
    }
    if (!skipValidation) validateItemSize(true);
  };

  const handleSplitterBars = () => {
    for (let i = 0; i < splitterItems.current.length; i++) {
      const item = splitterItems.current[i];
      item.style[measurements.current.restrictedDimension.toLowerCase() as "height" | "width"] = "100%";
      item.style[("max" + measurements.current.restrictedDimension) as "maxHeight" | "maxWidth"] = "none";
      if (!minMaxSizeItems.current[i].size) {
        item.style[measurements.current.dimension] = sizeItemsBeforeCollapse.current[i]
          ? sizeItemsBeforeCollapse.current[i] + "px"
          : "";
      }
      item.style[measurements.current.maxDimension] = minMaxSizeItems.current[i].max
        ? sizeLimitsItems.current[i][measurements.current.maxDimension] + "px"
        : "";
      if (collapsedSplitterItems.current[i]) {
        item.style.minWidth = "";
        item.style.minHeight = "";
        item.style[measurements.current.minDimension] = "0";
        ignorePropertyValueItems.current[i] = true;
        collapse(i);
      } else {
        item.style[measurements.current.minDimension] =
          sizeLimitsItems.current[i] && minMaxSizeItems.current[i].min
            ? sizeLimitsItems.current[i][measurements.current.minDimension] + "px"
            : "";
      }
    }
  };

  const validateItemsSizeUnderflowing = (remainingSize: number, isResizing?: boolean) => {
    const totalItems = splitterItems.current.length;
    let currentSize,
      newSize,
      availableSize = 0;
    remainingSize = Math.abs(remainingSize);
    for (let i = 0; i < totalItems; i++) {
      const item = splitterItems.current[i];
      if (
        !collapsedSplitterItems.current[i] &&
        item[measurements.current.size] <
          (sizeLimitsItems.current[i] ? sizeLimitsItems.current[i][measurements.current.minDimension] : 0)
      ) {
        currentSize = item[measurements.current.size];
        availableSize = currentSize + remainingSize;
        if (currentSize < sizeLimitsItems.current[i][measurements.current.minDimension]) {
          sizeLimitsItems.current[i].ignoreUpdate = !isResizing;
          item.style[measurements.current.minDimension] =
            (sizeItemsBeforeCollapse.current[i] = Math.max(
              0,
              Math.min(sizeLimitsItems.current[i][measurements.current.minDimension], availableSize),
            )) + "px";
        }
        remainingSize -= (sizeItemsBeforeCollapse.current[i] || item[measurements.current.size]) - currentSize;
        if (remainingSize <= 0) break;
      }
    }
    const containerSize = sizeSplitterContainer.current[measurements.current.dimension];
    if (remainingSize > 0) {
      for (let i = 0; i < totalItems; i++) {
        const item = splitterItems.current[i];
        if (!collapsedSplitterItems.current[i] && originalSizeItems.current[i] !== undefined) {
          const originalSize =
            typeof originalSizeItems.current[i] === "number"
              ? originalSizeItems.current[i].toString()
              : (originalSizeItems.current[i] as string);
          if (originalSize.indexOf("%") > -1) {
            const originalSizeInPixels = (parseFloat(originalSize) * containerSize) / 100;
            newSize = Math.min(originalSizeInPixels, item[measurements.current.size] + remainingSize);
            const sizeDifference = newSize - item[measurements.current.size];
            if (
              sizeLimitsItems.current[i][measurements.current.minDimension] <
              sizeLimitsItems.current[i][measurements.current.minDimension]
            ) {
              item.style[measurements.current.minDimension] =
                Math.min(sizeLimitsItems.current[i][measurements.current.minDimension], newSize) + "px";
            }
            item.style[measurements.current.dimension] =
              newSize === originalSizeInPixels ? originalSize : newSize + "px";
            sizeItemsBeforeCollapse.current[i] = Math.max(0, newSize);
            remainingSize -= sizeDifference;
          } else {
            const originalSize =
              typeof originalSizeItems.current[i] === "string"
                ? parseFloat(originalSizeItems.current[i] as string)
                : (originalSizeItems.current[i] as number);
            if (item[measurements.current.size] >= originalSizeItems.current[i]) {
              remainingSize += item[measurements.current.size] - originalSize;
              item.style[measurements.current.dimension] = (sizeItemsBeforeCollapse.current[i] = originalSize) + "px";
            } else {
              newSize = Math.min(originalSize, item[measurements.current.size] + remainingSize);
              const sizeDifference = newSize - item[measurements.current.size];
              item.style[measurements.current.dimension] =
                (sizeItemsBeforeCollapse.current[i] = Math.max(0, Math.min(newSize, originalSize))) + "px";
              remainingSize -= sizeDifference;
            }
          }
        }
        if (remainingSize <= 0) break;
      }
    }
  };

  const validateItemLimits = (itemIndex: number, newSize: number) => {
    const element = splitterItems.current[itemIndex];
    const containerSize = splitterRef.current.parentElement!.getBoundingClientRect()[measurements.current.dimension];
    const minLimit = minMaxSizeItems.current[itemIndex]._min || minMaxSizeItems.current[itemIndex].min;
    if (minLimit) {
      const isPercent = typeof minLimit === "string" && minLimit.includes("%");
      const minValue = isPercent ? (parseFloat(minLimit) * containerSize) / 100 : parseFloat(minLimit);
      if (minValue > newSize) {
        element.style[measurements.current.minDimension] = isPercent
          ? `${(100 * newSize) / containerSize}%`
          : `${newSize}px`;
        sizeLimitsItems.current[itemIndex][measurements.current.minDimension] = newSize;
        minMaxSizeItems.current[itemIndex]._min = minLimit;
        minMaxSizeItems.current[itemIndex].min = element.style[measurements.current.minDimension];
      } else if (minValue < newSize) {
        element.style[measurements.current.minDimension] = isPercent
          ? `${(100 * minValue) / containerSize}%`
          : `${minValue}px`;
        sizeLimitsItems.current[itemIndex][measurements.current.minDimension] = minValue;
        minMaxSizeItems.current[itemIndex].min = minLimit;
        delete minMaxSizeItems.current[itemIndex]._min;
      }
    }
    const maxLimit = minMaxSizeItems.current[itemIndex]._max || minMaxSizeItems.current[itemIndex].max;
    if (maxLimit) {
      const isPercent = typeof maxLimit === "string" && maxLimit.includes("%");
      const maxValue = isPercent ? (parseFloat(maxLimit) * containerSize) / 100 : parseFloat(maxLimit);
      if (maxValue < newSize) {
        element.style[measurements.current.maxDimension] = isPercent
          ? `${(100 * newSize) / containerSize}%`
          : `${newSize}px`;
        sizeLimitsItems.current[itemIndex][measurements.current.maxDimension] = newSize;
        minMaxSizeItems.current[itemIndex]._max = maxLimit;
        minMaxSizeItems.current[itemIndex].max = element.style[measurements.current.maxDimension];
      } else if (maxValue > newSize) {
        element.style[measurements.current.maxDimension] = isPercent
          ? `${(100 * maxValue) / containerSize}%`
          : `${maxValue}px`;
        sizeLimitsItems.current[itemIndex][measurements.current.maxDimension] = maxValue;
        minMaxSizeItems.current[itemIndex].max = maxLimit;
        delete minMaxSizeItems.current[itemIndex]._max;
      }
    }
  };

  const autoFitLastItem = (itemIndex: number) => {
    const referenceItem = splitterItems.current[itemIndex];
    const totalItems = splitterItems.current.length;
    if (totalItems === 1) {
      setSize("size", "100%", itemIndex, true);
      delete originalSizeItems.current[itemIndex];
    } else {
      let totalSize = 0,
        totalBarsSize = 0,
        hiddensBarsSize = 0;
      splitterItems.current.forEach((item, index) => {
        if (collapsedSplitterItems.current[index]) return;
        const sizeStr = item.style[measurements.current.dimension];
        if (sizeStr && sizeStr.indexOf("%") < -1 && sizeItemsBeforeCollapse.current[index]) {
          totalSize += sizeItemsBeforeCollapse.current[index];
        } else {
          totalSize += item[measurements.current.size];
        }
      });
      splitterBars.current.forEach((bar) => {
        if (bar.hasAttribute("data-itemcollapsed") && props.hideSplitterCollapsed) {
          hiddensBarsSize += smetaSplitterBarSize;
        } else totalBarsSize += bar[measurements.current.size];
      });
      const totalCombinedSize = totalSize + totalBarsSize;
      const containerSize = sizeSplitterContainer.current[measurements.current.dimension];
      if (totalCombinedSize !== containerSize) {
        const currentSize =
          referenceItem.style[measurements.current.dimension].indexOf("%") < -1 &&
          sizeItemsBeforeCollapse.current[itemIndex]
            ? sizeItemsBeforeCollapse.current[itemIndex]
            : referenceItem[measurements.current.size];
        const diff = Math.abs(containerSize - totalCombinedSize);
        const direction = totalCombinedSize < containerSize ? 1 : -1;
        const newSize = Math.max(0, currentSize + direction * diff);
        sizeItemsBeforeCollapse.current[itemIndex] = newSize - hiddensBarsSize;
        referenceItem.style[measurements.current.dimension] = newSize + "px";
        delete originalSizeItems.current[itemIndex];
        validateItemLimits(itemIndex, newSize);
      }
    }
  };

  const autoFitItems = () => {
    const totalItems = splitterItems.current.length;
    if (totalItems === 0) return;
    let referenceItem: number | undefined;
    const collapsedItems: number[] = [],
      sizedItems: number[] = [];
    for (let i = totalItems - 1; i >= 0; i--) {
      const minMaxSizeChild = minMaxSizeItems.current[i];
      if (collapsedSplitterItems.current[i]) {
        collapsedItems.push(i);
      } else if (referenceItem !== undefined) {
        if (!minMaxSizeChild.size) sizedItems.push(i);
      } else {
        referenceItem = i;
      }
    }
    if (referenceItem !== undefined && minMaxSizeItems.current[referenceItem].size && sizedItems.length > 0) {
      referenceItem =
        sizedItems.find(
          (item) =>
            !minMaxSizeItems.current[item].max && !sizeLimitsItems.current[item][measurements.current.maxDimension],
        ) || referenceItem;
    }
    autoFitLastItem(referenceItem!);
  };

  const validateBarsSize = () => {};

  const keepItemProportionsOnResize = () => {
    let totalSize = 0;
    const visibleItems: number[] = [];
    for (let index = 0; index < splitterItems.current.length; index++) {
      const currentItem = splitterItems.current[index];
      if (collapsedSplitterItems.current[index]) continue;
      visibleItems.push(index);
      totalSize += sizeItemsBeforeCollapse.current[index] || currentItem[measurements.current.size];
    }
    if (splitterSize.current) totalSize = splitterSize.current;
    if (splitterItems.current.length === 1) return;
    const containerSize = splitterRef.current.getBoundingClientRect()[measurements.current.dimension];
    for (let index = 0; index < visibleItems.length; index++) {
      const currentItemIndex = visibleItems[index];
      const currentItem = splitterItems.current[currentItemIndex];
      const currentItemStyleSize = currentItem.style[measurements.current.dimension];
      const currentItemStyleMinSize = currentItem.style[measurements.current.minDimension];
      if (currentItemStyleSize.indexOf("%") > -1 || currentItemStyleMinSize.indexOf("%") > -1) {
        validateItemLimits(currentItemIndex, (currentItem[measurements.current.size] / totalSize) * containerSize);
        continue;
      }
      const itemSizeBeforeCollapse =
        sizeItemsBeforeCollapse.current[currentItemIndex] || currentItem[measurements.current.size];
      const newItemSize = (itemSizeBeforeCollapse / totalSize) * containerSize;
      if (itemSizeBeforeCollapse !== newItemSize) {
        currentItem.style[measurements.current.dimension] = newItemSize + "px";
        if (containerSize !== splitterRef.current.getBoundingClientRect()[measurements.current.dimension]) {
          currentItem.style[measurements.current.dimension] = itemSizeBeforeCollapse + "px";
          return;
        }
        currentItem.style[measurements.current.dimension] =
          (sizeItemsBeforeCollapse.current[currentItemIndex] = newItemSize) + "px";
        validateItemLimits(currentItemIndex, newItemSize);
      }
    }
    props.onResizeEnd?.(splitterRef.current);
  };

  const validateItemsSizeOverflowing = (remainingSize: number, isResizing?: boolean) => {
    const itemCount = splitterItems.current.length;
    let availableShrink = remainingSize;
    let adjustedSize = 0;
    let itemComputedSize, sizeString, sizeIsAuto;
    for (let i = 0; i < itemCount; i++) {
      const item = splitterItems.current[i];
      sizeString = item.style[measurements.current.dimension];
      if (!sizeString) sizeString = "auto";
      itemComputedSize = sizeString.includes("%") ? sizeString : item[measurements.current.size];
      if (!sizeString && !minMaxSizeItems.current[i].size && Number(minMaxSizeItems.current[i].size) !== 0) {
        delete originalSizeItems.current[i];
      }
      sizeIsAuto =
        (itemComputedSize === "auto" &&
          minMaxSizeItems.current[i].size &&
          isNaN(parseFloat(minMaxSizeItems.current[i].size))) ||
        minMaxSizeItems.current[i].size === "auto";
      originalSizeItems.current[i] =
        originalSizeItems.current[i] && !sizeIsAuto ? originalSizeItems.current[i] : itemComputedSize;
    }
    for (let i = itemCount - 1; i >= 0; i--) {
      const item = splitterItems.current[i];
      if (collapsedSplitterItems.current[i] || availableShrink <= 0) continue;
      const originalSize = String(originalSizeItems.current[i]);
      if (originalSize.includes("%")) {
        let sizeInPixels = item.style[measurements.current.dimension] || item[measurements.current.size];
        if (typeof sizeInPixels === "string") {
          if (sizeInPixels.includes("%")) {
            sizeInPixels =
              (parseFloat(sizeInPixels) / 100) * sizeSplitterContainer.current[measurements.current.dimension];
          } else sizeInPixels = parseFloat(sizeInPixels);
        }
        item.style[measurements.current.dimension] = originalSize;
        sizeItemsBeforeCollapse.current[i] =
          (sizeSplitterContainer.current[measurements.current.dimension] * parseFloat(originalSize)) / 100;
        availableShrink -= sizeInPixels - sizeItemsBeforeCollapse.current[i];
      } else {
        const currentSize = item[measurements.current.size];
        adjustedSize = currentSize - availableShrink;
        const minSize = sizeLimitsItems.current[i] ? sizeLimitsItems.current[i][measurements.current.minDimension] : 0;
        sizeItemsBeforeCollapse.current[i] = Math.max(minSize, adjustedSize);
        item.style[measurements.current.dimension] = sizeItemsBeforeCollapse.current[i] + "px";
        availableShrink -= currentSize - sizeItemsBeforeCollapse.current[i];
      }
    }
    if (availableShrink > 0) {
      for (let i = itemCount - 1; i >= 0; i--) {
        const item = splitterItems.current[i];
        if (collapsedSplitterItems.current[i]) continue;
        const currentSize = item[measurements.current.size];
        adjustedSize = currentSize - availableShrink;
        let minSize = sizeLimitsItems.current[i][measurements.current.minDimension] || minMaxSizeItems.current[i].min;
        if (minSize) {
          if (typeof minSize === "string") {
            if (minSize.includes("%")) {
              minSize = (parseFloat(minSize) / 100) * splitterRef.current[measurements.current.size];
            } else minSize = parseFloat(minSize);
          }
          if (minSize > adjustedSize) {
            sizeLimitsItems.current[i].ignoreUpdate = !isResizing;
            item.style[measurements.current.minDimension] = Math.max(0, adjustedSize) + "px";
          }
        }
        if (
          availableShrink === 0 &&
          originalSizeItems.current[i] &&
          String(originalSizeItems.current[i]).includes("%")
        ) {
          sizeLimitsItems.current[i].ignoreUpdate = !isResizing;
          sizeItemsBeforeCollapse.current[i] = Math.max(0, adjustedSize);
          item.style[measurements.current.dimension] = sizeItemsBeforeCollapse.current[i] + "px";
          availableShrink -= currentSize - sizeItemsBeforeCollapse.current[i];
        }
      }
    }
  };

  const validateItemSize = (isResizing?: boolean) => {
    const dimension = measurements.current.dimension;
    const containerSize = sizeSplitterContainer.current[dimension];
    const isPercentage = (value: string) => value.includes("%");
    let totalSize = 0;
    for (let i = 0; i < splitterItems.current.length; i++) {
      const item = splitterItems.current[i];
      const minMaxSizeChild = minMaxSizeItems.current[i];
      if (minMaxSizeChild.min && isPercentage(minMaxSizeChild.min)) {
        sizeLimitsItems.current[i][measurements.current.minDimension] =
          (parseFloat(minMaxSizeChild.min) * containerSize) / 100;
      }
      if (minMaxSizeChild.max && isPercentage(minMaxSizeChild.max)) {
        sizeLimitsItems.current[i][measurements.current.maxDimension] =
          (parseFloat(minMaxSizeChild.max) * containerSize) / 100;
      }
      if (!collapsedSplitterItems.current[i]) {
        const itemSize = item.style[dimension];
        totalSize +=
          itemSize && itemSize.indexOf("%") < 0 && sizeItemsBeforeCollapse.current[i]
            ? sizeItemsBeforeCollapse.current[i]
            : item[measurements.current.size];
      }
    }
    if (props.keepProportionsOnResize !== false && resizeEventFired.current) {
      keepItemProportionsOnResize();
    } else {
      const overflowSize =
        totalSize + splitterBars.current.reduce((sum, bar) => sum + bar[measurements.current.size], 0) - containerSize;
      // Если есть переполнение
      if (overflowSize > 0) {
        validateItemsSizeOverflowing(overflowSize, isResizing);
      }
      // Если недостает места
      else if (overflowSize < 0) {
        validateItemsSizeUnderflowing(overflowSize, isResizing);
      }
    }
    autoFitItems();
    validateBarsSize();
    splitterSize.current = containerSize;
  };

  const handleResize = () => {
    clearTimeout(resizeTimeout.current);
    resizeTimeout.current = setTimeout(
      () => {
        const splitterContainer = splitterRef.current;
        if (splitterContainer) {
          const newWidth = splitterContainer.offsetWidth,
            newHeight = splitterContainer.offsetHeight;
          if (
            (newWidth !== sizeSplitterContainer.current.width || newHeight !== sizeSplitterContainer.current.height) &&
            (newWidth !== 0 || newHeight !== 0)
          ) {
            sizeSplitterContainer.current = {
              width: newWidth,
              height: newHeight,
            };
            resizeEventFired.current = true;
            validateItemSize();
            resizeEventFired.current = false;
            props.onResizeEnd?.(splitterRef.current);
          }
        }
        resizeTimeout.current = undefined;
      },
      animationsOn ? 251 : 0,
    );
  };

  const completeResizing = () => {
    if (!dragDetails.current) return;
    if (splitBarDummy.current && splitterRef.current) {
      const firstItemIndex = dragDetails.current.firstItem;
      const first = splitterItems.current[firstItemIndex];
      sizeItemsBeforeCollapse.current[firstItemIndex] =
        currentSizeItems.current[firstItemIndex] + paddingsItems.current[firstItemIndex];
      first.style[measurements.current.dimension] = sizeItemsBeforeCollapse.current[firstItemIndex] + "px";
      delete originalSizeItems.current[firstItemIndex];
      const secondItemIndex = dragDetails.current.secondItem;
      const second = splitterItems.current[secondItemIndex];
      sizeItemsBeforeCollapse.current[secondItemIndex] =
        dragDetails.current.splitAreaSize -
        currentSizeItems.current[firstItemIndex] +
        paddingsItems.current[secondItemIndex];
      second.style[measurements.current.dimension] = sizeItemsBeforeCollapse.current[secondItemIndex] + "px";
      delete originalSizeItems.current[secondItemIndex];
      validateBarsSize();
      splitBarDummy.current.classList.remove(styles.limitReached);
      splitBarDummy.current.remove();
      splitBarDummy.current = undefined;
      splitterBackgroundFeedback.current?.remove();
      splitterBackgroundFeedback.current = undefined;
      props.onResizeEnd?.(splitterRef.current);
    }
    dragDetails.current = undefined;
  };

  const documentUpHandler = (ev: PointerEvent) => {
    const target =
      ev.target instanceof SVGPathElement || ev.target instanceof SVGElement ? ev.target.closest("button") : ev.target;
    completeResizing();
    if (collapseButtonPressed.current) {
      if (target === collapseButtonPressed.current.target) {
        if (collapsedSplitterItems.current[collapseButtonPressed.current.item]) {
          expand(collapseButtonPressed.current.item);
          setTimeout(
            () => {
              props.onExpand?.(splitterRef.current);
            },
            animationsOn ? transitionDuration + 1 : 0,
          );
        } else {
          collapse(collapseButtonPressed.current.item, collapseButtonPressed.current.farCollapse);
          setTimeout(
            () => {
              props.onCollapse?.(splitterRef.current);
            },
            animationsOn ? transitionDuration + 1 : 0,
          );
        }
      }
      collapseButtonPressed.current = undefined;
    }
  };

  const getTargetItem = (
    barElement: HTMLDivElement,
    elementSibling: "previousElementSibling" | "nextElementSibling",
    getLatest?: boolean,
  ) => {
    let splitterItem = getLatest ? splitterItems.current[splitterItems.current.length - 1] : barElement[elementSibling];
    while (splitterItem) {
      if (
        splitterItem instanceof HTMLDivElement &&
        splitterItem.classList.contains(styles.splitterItem) &&
        !collapsedSplitterItems.current[Number(splitterItem.dataset.splitteritemindex)]
      ) {
        return Number(splitterItem.dataset.splitteritemindex);
      }
      splitterItem = splitterItem[elementSibling];
    }
  };

  const setAdjacentOrEndDetails = (barElement: HTMLDivElement) => {
    const secondItem = getTargetItem(barElement, "nextElementSibling");
    if (secondItem === undefined || !dragDetails.current) {
      dragDetails.current = undefined;
      return true;
    }
    dragDetails.current.secondItem = secondItem;
    minMaxSizeItems.current[secondItem].size = "";
    currentSizeItems.current[secondItem] = splitterItems.current[secondItem][measurements.current.size];
    originalSizeItems.current[secondItem] = currentSizeItems.current[secondItem];
    const computedStyles = getComputedStyle(splitterItems.current[secondItem]);
    paddingsItems.current[secondItem] =
      (parseFloat(computedStyles.getPropertyValue("padding-" + measurements.current.position)) || 0) +
      (parseFloat(computedStyles.getPropertyValue("padding-" + measurements.current.position2)) || 0);
    currentSizeItems.current[secondItem] -= paddingsItems.current[secondItem];
    dragDetails.current.splitAreaSize =
      currentSizeItems.current[dragDetails.current.firstItem] + currentSizeItems.current[secondItem];
    dragDetails.current.secondItemTotalMaxSize = sizeLimitsItems.current[secondItem][measurements.current.maxDimension];
    dragDetails.current.secondItemTotalMinSize = sizeLimitsItems.current[secondItem][measurements.current.minDimension];
  };

  const setDragDetails = (barElement: HTMLDivElement, ev: React.PointerEvent) => {
    dragDetails.current = {} as ObjectDragDetails;
    dragDetails.current.scrollAmount = splitterRef.current[measurements.current.scroll];
    dragDetails.current.lockedItemsSize = 0;
    const firstItem = getTargetItem(barElement, "previousElementSibling");
    if (firstItem === undefined) {
      dragDetails.current = undefined;
      return;
    }
    dragDetails.current.firstItem = firstItem;
    minMaxSizeItems.current[firstItem].size = "";
    currentSizeItems.current[firstItem] = currentSizeItems.current[firstItem] =
      splitterItems.current[firstItem][measurements.current.size];
    originalSizeItems.current[firstItem] = currentSizeItems.current[firstItem];
    const computedStyle = getComputedStyle(splitterItems.current[firstItem]);
    const paddings =
      (parseFloat(computedStyle.getPropertyValue("padding-" + measurements.current.position)) || 0) +
      (parseFloat(computedStyle.getPropertyValue("padding-" + measurements.current.position2)) || 0);
    paddingsItems.current[firstItem] = paddings;
    currentSizeItems.current[firstItem] -= paddings;
    dragDetails.current.splitAreaSize = 0;
    dragDetails.current.secondItemTotalMaxSize = 0;
    dragDetails.current.secondItemTotalMinSize = 0;
    if (setAdjacentOrEndDetails(barElement)) return;
    const barOffset = barElement.getBoundingClientRect()[measurements.current.position];
    dragDetails.current.position = ev[measurements.current.pagePosition];
    dragDetails.current.splitBarOffset = dragDetails.current.position - barOffset;
    if (!splitBarDummy.current) {
      splitBarDummy.current = document.createElement("div");
      splitBarDummy.current.classList.add(styles.splitterBarFeedback);
    }
    if (!splitterBackgroundFeedback.current) {
      splitterBackgroundFeedback.current = document.createElement("div");
      splitterBackgroundFeedback.current.classList.add(styles.splitterBackgroundFeedback);
    }
    splitBarDummy.current.style.width = barElement.offsetWidth + "px";
    splitBarDummy.current.style.height = barElement.offsetHeight + "px";
    splitBarDummy.current.style.top = barElement.offsetTop + "px";
    splitBarDummy.current.style.left = barElement.offsetLeft + "px";
    splitterRef.current.appendChild(splitBarDummy.current);
    document.body.appendChild(splitterBackgroundFeedback.current);
  };

  const resizeItem = (ev: PointerEvent, delta: number, offset: number) => {
    if (!dragDetails.current) return;
    const firstItem = dragDetails.current.firstItem;
    const secondItem = dragDetails.current.secondItem;
    const minFirstSize = sizeLimitsItems.current[firstItem][measurements.current.minDimension] || 0;
    const minSecondSize = sizeLimitsItems.current[secondItem][measurements.current.minDimension] || 0;
    let newSize = Math.max(
      minFirstSize,
      Math.min(
        dragDetails.current.splitAreaSize - minSecondSize,
        sizeLimitsItems.current[firstItem][measurements.current.maxDimension]
          ? Math.min(
              sizeLimitsItems.current[firstItem][measurements.current.maxDimension],
              currentSizeItems.current[firstItem] + delta,
            )
          : currentSizeItems.current[firstItem] + delta,
      ),
    );
    if (
      sizeLimitsItems.current[secondItem][measurements.current.maxDimension] &&
      dragDetails.current.splitAreaSize - newSize >
        sizeLimitsItems.current[secondItem][measurements.current.maxDimension]
    ) {
      newSize =
        dragDetails.current.splitAreaSize - sizeLimitsItems.current[secondItem][measurements.current.maxDimension];
    }
    currentSizeItems.current[firstItem] = newSize;
    splitBarDummy.current!.style[measurements.current.position] =
      splitterItems.current[firstItem][measurements.current.offset] +
      newSize +
      dragDetails.current.lockedItemsSize +
      paddingsItems.current[firstItem] +
      "px";
    const firstItemPosition =
      splitterItems.current[firstItem].getBoundingClientRect()[props.orientation === "vertical" ? "left" : "top"] +
      dragDetails.current.splitBarOffset;
    dragDetails.current.position = Math.max(
      firstItemPosition + minFirstSize + paddingsItems.current[firstItem],
      Math.min(
        firstItemPosition +
          dragDetails.current.splitAreaSize -
          minSecondSize +
          dragDetails.current.lockedItemsSize +
          paddingsItems.current[firstItem],
        ev[measurements.current.pagePosition] - offset,
      ),
    );
    if (sizeLimitsItems.current[firstItem][measurements.current.maxDimension]) {
      dragDetails.current.position = Math.min(
        firstItemPosition + sizeLimitsItems.current[firstItem][measurements.current.maxDimension],
        dragDetails.current.position,
      );
    }
  };

  const resize = (ev: PointerEvent) => {
    if (!dragDetails.current) return;
    const resizeStep = 1;
    let delta = ev[measurements.current.pagePosition] - dragDetails.current.position;
    const direction = Math.sign(delta);
    const minSize = sizeLimitsItems.current[dragDetails.current.firstItem][measurements.current.minDimension];
    const maxSize = sizeLimitsItems.current[dragDetails.current.firstItem][measurements.current.maxDimension];
    let availableSizeForResize: number, isWithinLimits, checkResizeLimitReached;
    if (Math.abs(delta) < resizeStep) return;
    let resizeAmount = Math.max(resizeStep, Math.floor(Math.abs(delta) / resizeStep) * resizeStep);
    const remainingOffset = delta - direction * resizeAmount;
    if (direction > 0) {
      availableSizeForResize =
        dragDetails.current.splitAreaSize - currentSizeItems.current[dragDetails.current.firstItem];
      isWithinLimits = () =>
        availableSizeForResize - dragDetails.current!.secondItemTotalMinSize >= resizeAmount ||
        (maxSize && currentSizeItems.current[dragDetails.current!.firstItem] + resizeStep <= maxSize);
      checkResizeLimitReached = () =>
        (maxSize && currentSizeItems.current[dragDetails.current!.firstItem] === maxSize) ||
        availableSizeForResize - resizeStep <= dragDetails.current!.secondItemTotalMinSize;
    } else {
      availableSizeForResize = currentSizeItems.current[dragDetails.current.firstItem];
      isWithinLimits = () =>
        availableSizeForResize - minSize >= resizeAmount ||
        (dragDetails.current!.secondItemTotalMaxSize &&
          dragDetails.current!.splitAreaSize - currentSizeItems.current[dragDetails.current!.firstItem] + resizeStep <=
            dragDetails.current!.secondItemTotalMaxSize);
      checkResizeLimitReached = () =>
        (dragDetails.current!.secondItemTotalMaxSize &&
          dragDetails.current!.splitAreaSize - currentSizeItems.current[dragDetails.current!.firstItem] ===
            dragDetails.current!.secondItemTotalMaxSize) ||
        availableSizeForResize - resizeStep <= minSize;
    }
    delta = 0;
    while (resizeAmount > 0) {
      if (isWithinLimits()) delta += direction * resizeStep;
      resizeAmount -= resizeStep;
    }
    resizeItem(ev, delta, remainingOffset);
    if (checkResizeLimitReached()) {
      splitBarDummy.current!.classList.add(styles.limitReached);
    } else {
      splitBarDummy.current!.classList.remove(styles.limitReached);
    }
    validateBarsSize();
  };

  const downHandler = (ev: React.PointerEvent) => {
    const target =
      ev.target instanceof SVGPathElement || ev.target instanceof SVGElement ? ev.target.closest("button") : ev.target;
    if (
      target instanceof HTMLButtonElement &&
      (target.classList.contains(styles.splitterFarCollapseButton) ||
        target.classList.contains(styles.splitterNearCollapseButton))
    ) {
      ev.stopPropagation();
      const splitBar = target.parentElement as HTMLDivElement;
      const splitItem = (
        target.classList.contains(styles.splitterFarCollapseButton)
          ? splitBar.nextElementSibling
          : splitBar.previousElementSibling
      ) as HTMLDivElement;
      collapseButtonPressed.current = {
        splitBar,
        item: Number(splitItem.dataset.splitteritemindex),
        target,
        farCollapse: target.classList.contains(styles.splitterFarCollapseButton),
      };
    } else if (target instanceof HTMLDivElement && target.classList.contains(styles.splitterBar)) {
      ev.stopPropagation();
      if (!target.hasAttribute("data-itemcollapsed") && !props.blockResize) setDragDetails(target, ev);
    }
  };

  const renderSmetaSplitterItem = () => {
    const childrenArray = getChildren(props.children);
    if (!childrenArray) return null;
    sizeLimitsItems.current = {};
    const smetaSplitterItemArray: React.ReactNode[] = [];
    const isSplitterReady = !splitterIsInitializing.current;
    childrenArray.forEach((child, i) => {
      const childProps = (child as React.ReactElement<SmetaSplitterItemProps>).props;
      const style: React.CSSProperties = {};
      const sizeLimitsProps = { min: childProps.min ?? "", max: childProps.max ?? "" };
      const isMinInPercent = typeof childProps.min === "string" && childProps.min.indexOf("%") > -1;
      const isMaxInPercent = typeof childProps.max === "string" && childProps.max.indexOf("%") > -1;
      let minValue = isNaN(parseFloat(sizeLimitsProps.min)) ? 0 : parseFloat(sizeLimitsProps.min);
      let maxValue = isNaN(parseFloat(sizeLimitsProps.max)) ? 0 : parseFloat(sizeLimitsProps.max);
      if (isMinInPercent && isSplitterReady)
        minValue = (minValue * splitterRef.current[measurements.current.size]) / 100;
      if (isMaxInPercent && isSplitterReady)
        maxValue = (maxValue * splitterRef.current[measurements.current.size]) / 100;
      sizeLimitsItems.current[i] = {
        minWidth: minValue,
        minHeight: minValue,
        maxWidth: maxValue,
        maxHeight: maxValue,
      };
      if (childProps.size) {
        const isSizeInPercent = childProps.size.indexOf("%") > -1;
        const parsedSize =
          childProps.size === "auto"
            ? "auto"
            : isNaN(parseFloat(childProps.size))
              ? 0
              : parseFloat(childProps.size) + (isSizeInPercent ? "%" : "px");
        if (isSplitterReady) {
          style[measurements.current.dimension] = parsedSize;
        } else {
          style.height = parsedSize;
          style.width = parsedSize;
        }
      }
      const combinedStyle: React.CSSProperties = {
        ...style,
        ...(childProps.style || {}),
      };
      smetaSplitterItemArray.push(
        <div
          className={styles.splitterItem}
          style={combinedStyle}
          role="presentation"
          key={`splitter-item-${i}`}
          data-splitteritemindex={i}
        >
          <SplitterIndexContext.Provider value={i}>{child}</SplitterIndexContext.Provider>
        </div>,
      );
      if (i < childrenArray.length - 1) {
        const collapseSide = childProps.collapseSide || getDefaultCollapseSide(primaryElement, props.orientation);
        smetaSplitterItemArray.push(
          <SmetaSplitterBar
            primaryIndex={primaryElement}
            hideSplitter={props.hideSplitter}
            blockResize={props.blockResize}
            hideButtons={props.hideButtons}
            key={`splitter-bar-${i}`}
            collapseSide={collapseSide}
          />,
        );
      }
    });
    return <SplitterContext.Provider value={propertyChangedHandler}>{smetaSplitterItemArray}</SplitterContext.Provider>;
  };

  return (
    <div
      role="group"
      className={styles.splitter}
      ref={(el) => {
        splitterRef.current = el as SmetaSplitterElement;
        if (props.splitterRef) props.splitterRef.current = el as SmetaSplitterElement;
      }}
      id={props.id}
      style={props.style}
      data-smetaresizer=""
      onPointerDown={downHandler}
    >
      <div
        role="presentation"
        className={clsx(
          styles.splitterContainer,
          props.orientation === "vertical" ? styles.splitterVertical : styles.splitterHorizontal,
        )}
      >
        {renderSmetaSplitterItem()}
      </div>
    </div>
  );
};
export default SmetaSplitter;
